/**
 * Conflict recovery: turning a refusal into a decision a person can actually make.
 *
 * WHY THIS IS SEPARATE FROM `conflictModel.ts`
 * --------------------------------------------
 * `conflictModel.ts` answers one question — "may this change be applied?" — and it
 * answers it the same way no matter who is asking. `documentLifecycleIdentity.test.ts`
 * pins those answers, and they must not be bent to serve a screen.
 *
 * This module answers the two questions the applier deliberately did not:
 *
 *  1. WHAT DO WE TELL THEM? The stored sentence used to say "changed on another
 *     device" for a conflict whose two sides came from the SAME device, which
 *     sends the user looking for a second machine that does not exist. It also
 *     called a DRAFT quotation an "issued document". Both are wrong in a way that
 *     costs trace time, and neither needs a new classification to fix — only the
 *     facts already on the row.
 *  2. WHAT MAY BE DONE ABOUT IT? In this milestone exactly one action: keep the
 *     local version and acknowledge. `USE_REMOTE` is deliberately absent — it is
 *     the action that writes a peer's payload over local rows, and it must arrive
 *     with its own validation rather than as a side effect of this one.
 *
 * WHAT AN ACKNOWLEDGEMENT DOES NOT DO
 * -----------------------------------
 * It closes the record on THIS device. It does not delete the other copy, and it
 * cannot promise the other device converges — that device has its own row and its
 * own position in the change log. Copy on screen must say so; a button that
 * implied convergence would be the same silent disagreement this whole layer
 * exists to prevent, only quieter.
 */
import type { SyncChange } from './changeModel';
import { guidanceFor, type ConflictReason, type SyncConflict } from './conflictModel';

/**
 * The largest refused change we keep verbatim.
 *
 * MEASURED, NOT GUESSED. Across the 30 changes in the recorded 2026-10-27 backup,
 * payloads run 363 B to 2,527 B (median 2,265 B); the largest is a document
 * carrying its `calcSnapshot`. 64 KB therefore keeps every conflict ever observed
 * by this product with ~25× headroom, while still bounding what a pathological or
 * hostile payload can pin inside IndexedDB forever.
 *
 * Above the cap the change is still recorded (identity and version), never
 * silently dropped — see `pendingChangeForStorage`.
 */
export const MAX_PENDING_CHANGE_BYTES = 64 * 1024;

/** Exactly how many bytes the stored copy of a change occupies, as JSON. */
export function pendingChangeBytes(change: SyncChange): number {
  return new TextEncoder().encode(JSON.stringify(change)).length;
}

export interface StoredPendingChange {
  readonly pendingChange: SyncChange;
  readonly pendingChangeBytes: number;
  readonly pendingChangeOmitted?: 'TOO_LARGE';
}

/**
 * The copy of the refused change to keep on the conflict row.
 *
 * An oversized change is degraded, not discarded: the payload is dropped and the
 * omission is RECORDED, because the identity and version are what acknowledging
 * needs, and a later remote-apply must be able to see that the payload is missing
 * rather than believe it was empty.
 */
export function pendingChangeForStorage(change: SyncChange): StoredPendingChange {
  const bytes = pendingChangeBytes(change);
  if (bytes > MAX_PENDING_CHANGE_BYTES) {
    return {
      pendingChange: { ...change, payload: undefined },
      pendingChangeBytes: bytes,
      pendingChangeOmitted: 'TOO_LARGE',
    };
  }
  return { pendingChange: change, pendingChangeBytes: bytes };
}

/**
 * The `status` a stored row or a change payload carries, when it carries one.
 *
 * Used for BOTH sides of a document conflict: the local row read from its table
 * and the refused version read from its payload. A sentence that knows only one
 * of them can only be wrong about the other.
 */
export function statusOfRecord(row: unknown): string | undefined {
  if (typeof row !== 'object' || row === null) return undefined;
  const status = (row as Record<string, unknown>)['status'];
  return typeof status === 'string' ? status : undefined;
}

/** The status a refused document version carries, when its payload has one. */
export function documentStatusOf(change?: SyncChange): string | undefined {
  return statusOfRecord(change?.payload);
}

export interface ConflictNarrative {
  readonly reason: ConflictReason;
  readonly entityType: string;
  readonly localDeviceId: string;
  readonly remoteDeviceId: string;
  /**
   * The status of the record on THIS device, read now.
   *
   * Unknown is a legitimate answer (a legacy row, a deleted record, a caller that
   * cannot read the table), and the wording then claims nothing about it.
   */
  readonly localStatus?: string;
  /** The status carried by the refused remote version, when it carries one. */
  readonly remoteStatus?: string;
}

/** A draft is a draft. Nothing about it is issued, and calling it issued is a lie. */
const DRAFT_SAME_DEVICE =
  'This device saved two different versions of a draft document. Nothing was overwritten. Choose which version is correct; a draft can still be edited before it is issued.';
const DRAFT_OTHER_DEVICE =
  'Two devices saved different versions of a draft document. Nothing was overwritten. Choose which version is correct; a draft can still be edited before it is issued.';

/**
 * An issued document, refused on the device that issued it.
 *
 * Says "this device" on purpose: the same-device case is usually a retry that
 * arrived without its change id, not a second shop rewriting an invoice.
 */
const ISSUED_SAME_DEVICE =
  'This device recorded a second, different version of an issued document. Issued documents are never overwritten. Compare the two, and use a credit note or cancellation to correct it.';

/**
 * A document LINE, whose payload carries no status.
 *
 * The parent document's status is not in the change, so the wording must not
 * assert one. "If the document was already issued" is true either way.
 */
const LINE_SAME_DEVICE =
  'This device recorded two different versions of a document line. Nothing was overwritten. Choose which version is correct; if the document was already issued, correct it with a credit note or cancellation.';
const LINE_OTHER_DEVICE =
  'A second, different version of a document line arrived from another device. Nothing was overwritten. If the document was already issued, correct it with a credit note or cancellation.';

/**
 * One side is a draft and the other is not, and both came from this device.
 *
 * Which side is the draft does not change the advice, so one sentence covers
 * both directions: the issued version is the record, and editing the draft cannot
 * replace it.
 */
const MIXED_ON_THIS_DEVICE =
  'This device holds two different versions of this document: one has been issued and one is still a draft. Nothing was overwritten. An issued document is corrected with a credit note or cancellation, not by editing the draft.';

/** The other device issued it while this one still holds a draft. */
const ISSUED_ELSEWHERE_LOCAL_DRAFT =
  'This document was issued on another device while this device still holds a draft. Nothing was overwritten. An issued document is corrected with a credit note or cancellation, not by editing the draft.';

/** This device issued it; the other device still holds a draft of it. */
const ISSUED_HERE_REMOTE_DRAFT =
  'Another device still holds a draft of a document that is already issued here. Nothing was overwritten. The issued version stays the record; correct it with a credit note or cancellation.';

/**
 * A document whose refused version kept NO status.
 *
 * This is the shape of every conflict recorded before changes were stored: the
 * row knows the entity and the versions, not what the document was. The wording
 * therefore names both outcomes instead of asserting the one that happens to be
 * more alarming.
 */
const UNKNOWN_SAME_DEVICE =
  'This device recorded a second, different version of this document. Nothing was overwritten. Choose which version is correct; if the document has already been issued, correct it with a credit note or cancellation.';
const UNKNOWN_OTHER_DEVICE =
  'A second, different version of this document arrived from another device. Nothing was overwritten. Compare the two; if the document has already been issued, correct it with a credit note or cancellation.';

const SAME_FIELD_SAME_DEVICE =
  'This device recorded two different versions of this record. Nothing was overwritten. Choose which version is correct.';
const LEDGER_SAME_DEVICE =
  'This device recorded conflicting stock movements. Stock is append-only, so nothing was changed. Check the ledger, then record a correction if one is wrong.';

/**
 * A document (or line) whose two versions were refused.
 *
 * BOTH statuses are needed, and neither alone is enough. The sentence was first
 * built from the reason, then from the incoming status only; either way one of
 * the two mismatches was described backwards — a draft on this device was told it
 * had been issued here, or an invoice issued here was offered as a draft that
 * could still be edited.
 */
function explainDocumentDivergence(input: ConflictNarrative, sameDevice: boolean): string {
  // Lines carry no status; their parent's is not in the change.
  if (input.entityType === 'documentLine') {
    return sameDevice ? LINE_SAME_DEVICE : LINE_OTHER_DEVICE;
  }

  const local = input.localStatus;
  const remote = input.remoteStatus;
  // Either side unknown: say what is true either way rather than assert a status
  // the row cannot prove. This is the legacy shape (no stored change) and the
  // deleted-record shape.
  if (local === undefined || remote === undefined) {
    return sameDevice ? UNKNOWN_SAME_DEVICE : UNKNOWN_OTHER_DEVICE;
  }

  const localDraft = local === 'DRAFT';
  const remoteDraft = remote === 'DRAFT';
  if (localDraft && remoteDraft) return sameDevice ? DRAFT_SAME_DEVICE : DRAFT_OTHER_DEVICE;
  if (localDraft) return sameDevice ? MIXED_ON_THIS_DEVICE : ISSUED_ELSEWHERE_LOCAL_DRAFT;
  if (remoteDraft) return sameDevice ? MIXED_ON_THIS_DEVICE : ISSUED_HERE_REMOTE_DRAFT;
  return sameDevice ? ISSUED_SAME_DEVICE : guidanceFor('FINALIZED_DIVERGENCE');
}

/**
 * What to say about a conflict, given everything the row can prove.
 *
 * The cross-device, finalized-document case still delegates to `guidanceFor`: that
 * sentence is accurate for it, and it is asserted by existing tests. Everything
 * this function adds is a case where the old sentence was FALSE:
 *
 *  - the two sides came from this device, so there is no "other device" to go and
 *    look at;
 *  - one side is a draft, so a sentence about an issued document may be wrong
 *    about this record (see `explainDocumentDivergence`).
 */
export function explainConflict(input: ConflictNarrative): string {
  const sameDevice = input.localDeviceId === input.remoteDeviceId;
  const isDocument = input.entityType === 'document' || input.entityType === 'documentLine';

  if (isDocument && input.reason === 'FINALIZED_DIVERGENCE') {
    return explainDocumentDivergence(input, sameDevice);
  }
  if (!sameDevice) return guidanceFor(input.reason);
  switch (input.reason) {
    case 'FINALIZED_DIVERGENCE':
      return ISSUED_SAME_DEVICE;
    case 'SAME_FIELD':
      return SAME_FIELD_SAME_DEVICE;
    case 'LEDGER_APPEND_ONLY':
      return LEDGER_SAME_DEVICE;
    default:
      return guidanceFor(input.reason);
  }
}

/**
 * The narrative for a stored conflict, using whatever the caller can prove.
 *
 * `localStatus` is what the local row says NOW; the screen reads it before
 * calling. Without it the sentence must not assert the local state, which is why
 * an unreadable row gets the wording that names both outcomes.
 *
 * The stored `guidance` is deliberately NOT the display source. It records what
 * was true when the conflict was DETECTED, and the record may have moved since —
 * which is exactly the case this milestone makes resolvable. Recomputing keeps a
 * single source of truth for the words on screen.
 */
export function explainStoredConflict(conflict: SyncConflict, localStatus?: string): string {
  return explainConflict({
    reason: conflict.reason,
    entityType: conflict.entityType,
    localDeviceId: conflict.localDeviceId,
    remoteDeviceId: conflict.remoteDeviceId,
    localStatus,
    remoteStatus: documentStatusOf(conflict.pendingChange),
  });
}

/**
 * What to say after a KEEP_LOCAL acknowledgement.
 *
 * It must not promise that the other device follows: nothing is written to the
 * peer, and no later sync makes two divergent copies agree on its own. The
 * sentence it replaces ended "until it syncs", which reads as exactly that
 * promise — and it is false.
 */
export const KEEP_LOCAL_ACKNOWLEDGEMENT_NOTICE =
  'The conflict is now marked resolved on this device, and this device keeps the version it has. ' +
  'The other device is not changed by that: it may keep its own version, and a later sync does not guarantee that the two will agree.';

/** Why an acknowledgement was refused. Each one is a fact, not a failure to try. */
export type KeepLocalRefusalCode = 'STALE_LOCAL' | 'CHANGE_MISMATCH';

/** Which version the person was actually shown when they chose to keep it. */
export type KeepLocalBasis = 'DETECTION_VERSION' | 'REVIEWED_VERSION';

export interface KeepLocalCheckInput {
  readonly conflict: SyncConflict;
  /**
   * `syncVersion` on the local row RIGHT NOW, read inside the resolving
   * transaction. 0 when the row is no longer there.
   */
  readonly currentLocalVersion: number;
  /**
   * The version the person has just been shown and confirmed.
   *
   * Supplied ONLY by a second, explicit acknowledgement after a `STALE_LOCAL`
   * refusal — never inferred, and never filled in by a caller that has not
   * actually displayed that version. See `planKeepLocal`.
   */
  readonly reviewedLocalVersion?: number;
}

export type KeepLocalCheck =
  | { readonly ok: true; readonly basis: KeepLocalBasis }
  | {
      readonly ok: false;
      readonly code: KeepLocalRefusalCode;
      readonly message: string;
      /** The version to review. Present for STALE_LOCAL, absent otherwise. */
      readonly currentLocalVersion?: number;
    };

/**
 * May the local version be kept, as things stand right now?
 *
 * TWO CHECKS, both about identity rather than about policy:
 *
 *  1. The stored change must still BE the change this conflict is about. An
 *     acknowledgement is tied to one (entity, version, change id); if the stored
 *     copy disagrees with the record, the record is not what it claims and must
 *     not be closed on the strength of it. This one has no override: a record
 *     that disagrees with its own change is corrupt, and no amount of reviewing
 *     makes it safe.
 *
 *  2. The local row must still be at the version the conflict was detected
 *     against. "Keep the version I have" means a specific version; if the row has
 *     moved on since, the person may be looking at a shape they have never seen.
 *
 * THE SECOND CHECK REFUSES ONCE, IT DOES NOT LOCK THE RECORD.
 * ------------------------------------------------------------
 * Refusing with no way forward leaves a warning nobody can ever clear: the change
 * was consumed by the cursor and is never delivered again, so nothing re-records
 * the conflict at the newer version. Editing the record — the most natural
 * response to being told it needs review — was enough to make a conflict
 * permanently un-actionable.
 *
 * So a second, explicit acknowledgement may name the version it is confirming
 * (`reviewedLocalVersion`). It is not an override: the caller has to send the
 * version it actually displayed, and if the row has moved on again the refusal
 * stands. What the row keeps is the ORIGINAL detection version — that is history,
 * not a value to rewrite — and the resolver records which basis was used.
 */
export function planKeepLocal(input: KeepLocalCheckInput): KeepLocalCheck {
  const pending = input.conflict.pendingChange;
  if (pending) {
    const sameChange =
      pending.businessId === input.conflict.businessId &&
      pending.entityType === input.conflict.entityType &&
      pending.entityId === input.conflict.entityId &&
      pending.version === input.conflict.remoteVersion &&
      typeof pending.changeId === 'string' &&
      pending.changeId.length > 0;
    if (!sameChange) {
      return {
        ok: false,
        code: 'CHANGE_MISMATCH',
        message:
          'The stored copy of the refused change no longer matches this conflict record, so it cannot be acknowledged safely. Run sync again to record the disagreement afresh.',
      };
    }
  }

  if (input.currentLocalVersion === input.conflict.localVersion) {
    return { ok: true, basis: 'DETECTION_VERSION' };
  }

  if (
    input.reviewedLocalVersion !== undefined &&
    input.reviewedLocalVersion === input.currentLocalVersion
  ) {
    return { ok: true, basis: 'REVIEWED_VERSION' };
  }

  return {
    ok: false,
    code: 'STALE_LOCAL',
    currentLocalVersion: input.currentLocalVersion,
    message:
      `This record changed on this device after the conflict was recorded ` +
      `(version ${input.currentLocalVersion} now, ${input.conflict.localVersion} when it was recorded). ` +
      `Nothing was written. Review the current version, then confirm again to keep it.`,
  };
}
